import { useCallback, useEffect, useState } from "react";

/// DivisionMode — el flujo operativo que el operador quiere ver.
/// Lo usan SupervisorPage, FleetPage y TrackingPage para alternar
/// entre la vista CONSUMO (chofer+ruta planificada del día) y SALUD
/// (repartidor self-dispatch, últimos N días con pedidos abiertos).
export type DivisionMode = "CONSUMO" | "SALUD";

const STORAGE_KEY = "micelio.divisionMode";

/// useDivisionMode — hook compartido. Persiste la selección en
/// sessionStorage para que cambiar de tab no la resetee; la pierde al
/// cerrar la pestaña. Default = 'CONSUMO'.
export function useDivisionMode(): [DivisionMode, (m: DivisionMode) => void] {
  const [mode, setMode] = useState<DivisionMode>(() => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      return stored === "SALUD" || stored === "CONSUMO" ? stored : "CONSUMO";
    } catch {
      return "CONSUMO";
    }
  });
  const update = useCallback((m: DivisionMode) => {
    setMode(m);
    try {
      sessionStorage.setItem(STORAGE_KEY, m);
    } catch {
      // sessionStorage no disponible (incógnito agresivo, etc.) —
      // seguimos en memoria local; nada crítico.
    }
  }, []);
  // Sync cross-tab. Si en otra pestaña abierta el operador cambia el
  // modo, las demás se actualizan. Útil cuando va saltando entre
  // operación en vivo y avance.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && (e.newValue === "SALUD" || e.newValue === "CONSUMO")) {
        setMode(e.newValue);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);
  return [mode, update];
}

/// DivisionToggle — pill con dos opciones (Consumo / Salud). Usa
/// `useDivisionMode` por arriba para mantener la selección consistente
/// entre las pantallas de supervisión.
export function DivisionToggle({
  value,
  onChange,
  hint,
}: {
  value: DivisionMode;
  onChange: (m: DivisionMode) => void;
  /// Hint opcional al lado del control (ej. "del día" / "últimos 3 días").
  hint?: string;
}) {
  return (
    <div className="inline-flex items-center gap-2">
      <div className="inline-flex rounded-md border border-border overflow-hidden">
        {(["CONSUMO", "SALUD"] as const).map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => onChange(m)}
            className={`px-3 py-1.5 text-xs font-semibold border-l first:border-l-0 border-border transition-colors ${
              value === m
                ? m === "SALUD"
                  ? "bg-accent text-white"
                  : "bg-primary text-white"
                : "bg-card text-ink hover:bg-page"
            }`}
          >
            {m === "CONSUMO" ? "Consumo" : "Salud"}
          </button>
        ))}
      </div>
      {hint && <span className="text-[11px] text-sub">{hint}</span>}
    </div>
  );
}
